import * as v from './validationRegex';

/**
 * **isAlphaNumeric**_(input)_<br>
 * Checks if input is alphanumeric.
 * @param { String } input: The input to check.
 * @returns { Array } Array containing error messages if check doesn't pass.
 */
export const isAlphaNumeric = (input: string) =>
  v.createValidation(
    input,
    v.IS_ALPHA_NUMERIC,
    v.IS_ALPHA_NUMERIC_INCLUDES,
    v.IS_ALPHA_NUMERIC_EXCLUDES,
  );

/**
 * **isAlphaNumericDashes**_(input)_<br>
 * Checks if input is alphanumeric with dashes.
 * @param { String } input: The input to check.
 * @returns { Array } Array containing error messages if check doesn't pass.
 */
export const isAlphaNumericDashes = (input: string) =>
  v.createValidation(
    input,
    v.IS_ALPHA_NUMERIC_DASHES,
    v.IS_ALPHA_NUMERIC_DASHES_INCLUDES,
    v.IS_ALPHA_NUMERIC_DASHES_EXCLUDES,
  );

/**
 * **isAlphaNumericDashesSpaces**_(input)_<br>
 * Checks if input is alphanumeric with dashes and spaces.
 * @param { String } input: The input to check.
 * @returns { Array } Array containing error messages if check doesn't pass.
 */
export const isAlphaNumericDashesSpaces = (input: string) =>
  v.createValidation(
    input,
    v.IS_ALPHA_NUMERIC_DASHES_SPACES,
    v.IS_ALPHA_NUMERIC_DASHES_SPACES_INCLUDES,
    v.IS_ALPHA_NUMERIC_DASHES_SPACES_EXCLUDES,
  );

/**
 * **isAlphaNumericSpaces**_(input)_<br>
 * Checks if input is alphanumeric with spaces.
 * @param { String } input: The input to check.
 * @returns { Array } Array containing error messages if check doesn't pass.
 */
export const isAlphaNumericSpaces = (input: string) =>
  v.createValidation(
    input,
    v.IS_ALPHA_NUMERIC_SPACES,
    v.IS_ALPHA_NUMERIC_SPACES_INCLUDES,
    v.IS_ALPHA_NUMERIC_SPACES_EXCLUDES,
  );

/**
 * **isAlphaNumericDashesPeriod**_(input)_<br>
 * Checks if input is alphanumeric with dashes, and periods.
 * @param { String } input: The input to check.
 * @returns { Array } Array containing error messages if check doesn't pass.
 */
export const isAlphaNumericDashesPeriod = (input: string) =>
  v.createValidation(
    input,
    v.IS_ALPHA_NUMERIC_DASHES_PERIOD,
    v.IS_ALPHA_NUMERIC_DASHES_PERIOD_INCLUDES,
    v.IS_ALPHA_NUMERIC_DASHES_PERIOD_EXCLUDES,
  );

/**
 * **isAlphaNumericPeriodUnderscores**_(input)_<br>
 * Checks if input is alphanumeric, with periods and underscores.
 * @param { String } input: The input to check.
 * @returns { Array } Array containing error messages if check doesn't pass.
 */
export const isAlphaNumericPeriodUnderscores = (input: string) =>
  v.createValidation(
    input,
    v.IS_ALPHA_NUMERIC_PERIOD_UNDERSCORE,
    v.IS_ALPHA_NUMERIC_PERIOD_UNDERSCORE_INCLUDES,
    v.IS_ALPHA_NUMERIC_PERIOD_UNDERSCORE_EXCLUDES,
  );

/**
 * **isValidEmail**_(input)_<br>
 * Checks if input is a valid email address
 * @param { String } input: The input to check.
 * @returns { Array } Array containing error messages if check doesn't pass.
 */
export const isValidEmail = (input: string) =>
  v.createValidation(input, v.IS_VALID_EMAIL, v.IS_VALID_EMAIL_INCLUDES, v.IS_VALID_EMAIL_EXCLUDES);

/**
 * **isValidUrl**_(input)_<br>
 * Checks if input is a valid url
 * @param { String } input: The input to check.
 * @returns { Array } Array containing error messages if check doesn't pass.
 */
export const isValidUrl = (input: string) =>
  v.createValidation(input, v.IS_VALID_URL, v.IS_VALID_URL_INCLUDES, v.IS_VALID_URL_EXCLUDES);

/**
 * **isValidDomain**_(input)_<br>
 * Checks if input is a valid domain name, regardless if it has a protocol or not.
 * This check does not include validating if it is using a valid top-level-domain (ie .com)
 * @param { String } input: The input to check.
 * @returns { Array } Array containing error messages if check doesn't pass.
 */
export const isValidDomain = (input: string) => {
  const newURL = v.HAS_PROTOCOL.test(input) ? input : `https://${input}`;
  return isValidUrl(newURL);
};

/**
 * **isValidUrlWithHandlebars**_(input)_<br>
 * Checks if input is a valid url, with handlebars optionally included
 * @param { String } input: The input to check.
 * @returns { Array } Array containing error messages if check doesn't pass.
 */
export const isValidUrlWithHandlebars = (input: string) =>
  v.createValidation(
    input,
    v.IS_VALID_URL_WITH_HANDLEBARS,
    v.IS_VALID_URL_WITH_HANDLEBARS_INCLUDES,
    v.IS_VALID_URL_WITH_HANDLEBARS_EXCLUDES,
  );

/**
 * **isValidUrlWithHandlebarsAnywhere**_(input)_<br>
 * Checks if input is a valid url, with handlebars optionally included anywhere
 * including in the top-level-domain location
 * @param { String } input: The input to check.
 * @returns { Array } Array containing error messages if check doesn't pass.
 */
export const isValidUrlWithHandlebarsAnywhere = (input: string) =>
  v.createValidation(
    input,
    v.IS_VALID_URL_WITH_HANDLEBARS_ANYWHERE,
    v.IS_VALID_URL_WITH_HANDLEBARS_INCLUDES,
    v.IS_VALID_URL_WITH_HANDLEBARS_EXCLUDES,
  );

/**
 * **isValidLink**_(input)_<br>
 * Checks if input is a valid url
 * @param { String } input: The input to check.
 * @returns { Array } Array containing error messages if check doesn't pass.
 */
export const isValidLink = (input: string) =>
  v.createValidation(input, v.IS_VALID_LINK, v.IS_VALID_LINK_INCLUDES, v.IS_VALID_LINK_EXCLUDES);

/**
 * **isValidHandlebar**_(input)_<br>
 * Checks if input is a valid handlebar
 * @param { String } input: The input to check.
 * @returns { Array } Array containing error messages if check doesn't pass.
 */
export const isValidHandlebar = (input: string) =>
  v.createValidation(
    input,
    v.IS_VALID_HANDLEBAR,
    v.IS_VALID_HANDLEBAR_INCLUDES,
    v.IS_VALID_HANDLEBAR_EXCLUDES,
  );

/**
 * **noSpaces**_(input)_<br>
 * Checks if input has no spaces.
 * @param { String } input: The input to check.
 * @returns { Array } Array containing error messages if check doesn't pass.
 */
export const noSpaces = (input: string) =>
  v.createValidation(input, v.NO_SPACES, v.NO_SPACES_INCLUDES, v.NO_SPACES_EXCLUDES);

/**
 * **noLeadingSpaces**_(input)_<br>
 * Checks if input has no leading spaces.
 * @param { String } input: The input to check.
 * @returns { Array } Array containing error messages if check doesn't pass.
 */
export const noLeadingSpaces = (input: string) =>
  input && input.slice(0, 1) === ' ' ? [v.NO_LEADING_SPACES_EXCLUDES] : [];

/**
 * **noTrailingSpaces**_(input)_<br>
 * Checks if input has no trailing spaces.
 * @param { String } input: The input to check.
 * @returns { Array } Array containing error messages if check doesn't pass.
 */
export const noTrailingSpaces = (input: string) =>
  input && input.slice(-1) === ' ' ? [v.NO_TRAILING_SPACES_EXCLUDES] : [];

/**
 * **noLeadingTrailingSpaces**_(input)_<br>
 * Checks if input has no leading nor trailing spaces.
 * @param { String } input: The input to check.
 * @returns { Array } Array containing error messages if check doesn't pass.
 */
export const noLeadingTrailingSpaces = (input: string) => [
  ...noLeadingSpaces(input),
  ...noTrailingSpaces(input),
];
